export type QuickAccessItem = {
  id: string
  name: string
  url: string
  isPinned?: boolean
}

export type QuickAccessPrefsV1 = {
  version: 1
  items: Array<{
    id: string
    isPinned: boolean
  }>
}

const QUICK_ACCESS_PREFS_VERSION = 1
const QUICK_ACCESS_STORAGE_PREFIX = "sidebar.quick-access.v1"
const GUEST_KEY = "guest"

function isQuickAccessPrefsV1(value: unknown): value is QuickAccessPrefsV1 {
  if (!value || typeof value !== "object") {
    return false
  }

  const candidate = value as QuickAccessPrefsV1
  if (candidate.version !== QUICK_ACCESS_PREFS_VERSION) {
    return false
  }

  if (!Array.isArray(candidate.items)) {
    return false
  }

  return candidate.items.every(
    (item) =>
      Boolean(item) &&
      typeof item.id === "string" &&
      typeof item.isPinned === "boolean",
  )
}

export function toQuickAccessPrefs(items: QuickAccessItem[]): QuickAccessPrefsV1 {
  return {
    version: QUICK_ACCESS_PREFS_VERSION,
    items: items.map((item) => ({
      id: item.id,
      isPinned: Boolean(item.isPinned),
    })),
  }
}

export function applyQuickAccessPrefs(
  defaultItems: QuickAccessItem[],
  rawPrefs: unknown,
): QuickAccessItem[] {
  if (!isQuickAccessPrefsV1(rawPrefs)) {
    return defaultItems
  }

  const defaultsById = new Map(defaultItems.map((item) => [item.id, item]))
  const mergedItems: QuickAccessItem[] = []

  for (const prefItem of rawPrefs.items) {
    const item = defaultsById.get(prefItem.id)
    if (!item) {
      continue
    }

    mergedItems.push({
      ...item,
      isPinned: prefItem.isPinned,
    })
    defaultsById.delete(prefItem.id)
  }

  for (const item of defaultItems) {
    if (defaultsById.has(item.id)) {
      mergedItems.push(item)
    }
  }

  return mergedItems
}

export function normalizeQuickAccessStorageUserKey(
  email: string | null | undefined,
): string {
  const normalized = email?.trim().toLowerCase()

  if (!normalized) {
    return GUEST_KEY
  }

  return normalized
}

export function getQuickAccessStorageKey(
  email: string | null | undefined,
): string {
  return `${QUICK_ACCESS_STORAGE_PREFIX}:${normalizeQuickAccessStorageUserKey(
    email,
  )}`
}
